import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const applyAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tempPassword, setTempPassword] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('projectlife_user');
    const storedToken = localStorage.getItem('projectlife_token');
    if (storedToken) applyAuthHeader(storedToken);
    if (storedUser) setUser(JSON.parse(storedUser));
    setIsLoading(false);
  }, []);

  const login = (userData, plainPassword, token) => {
    setUser(userData);
    localStorage.setItem('projectlife_user', JSON.stringify(userData));
    if (token) {
      localStorage.setItem('projectlife_token', token);
      applyAuthHeader(token);
    }
    if (plainPassword) setTempPassword(plainPassword);
  };

  const logout = () => {
    setUser(null);
    setTempPassword(null);
    localStorage.removeItem('projectlife_user');
    localStorage.removeItem('projectlife_token');
    applyAuthHeader(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => {
      const merged = { ...prev, ...updates };
      localStorage.setItem('projectlife_user', JSON.stringify(merged));
      return merged;
    });
    if (updates && updates.must_reset_password === false) setTempPassword(null);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    tempPassword,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
